/**
 * Actycrea: plane.esm.js (ES6)
 * MIT
 *
 * Drives the interactive 3D showcase ( Iberia100 module 7/12, `module-plane` ).
 *
 * The module previously scrubbed a video with the scroll; it now embeds an
 * interactive Sketchfab model that the visitor can orbit, zoom and pan. The
 * viewer is created client-side through the Sketchfab Viewer API: the server
 * prints an empty `<iframe>` plus the model UID and options as `data-*`
 * attributes, and this behaviour loads the API ( once ) and initialises each
 * `module-plane` found in the page.
 *
 * AUTOSTART: in Viewer API mode the model loads when `api.start()` is called,
 * NOT via the `autostart` embed param ( that param only applies to the plain
 * iframe-URL embed ). So autostart is honored here by controlling WHEN
 * `api.start()` runs: immediately when enabled, or deferred until the visitor
 * clicks the interaction shield when disabled.
 *
 * UI CONTROLS / ANNOTATIONS: these flags are forwarded to `init()`, but hiding
 * the Sketchfab viewer chrome ( controls, branding, annotations UI ) requires
 * a paid Sketchfab plan. On free accounts the flags are ignored server-side
 * and the controls stay visible — nothing can be done from here, as the
 * viewer runs inside a cross-origin iframe.
 *
 * SCROLL ( click-to-interact ): the viewer captures the mouse wheel to zoom,
 * which would trap the page scroll while the pointer is over the module. To
 * avoid it, a transparent shield sits on top of the ready viewer and swallows
 * pointer events, so the page keeps scrolling past. Clicking the shield
 * activates full interaction ( `is-active`, shield lets events through, wheel
 * zoom included ); moving the pointer out of the module deactivates it again,
 * restoring page scroll.
 *
 * The API script is injected lazily the first time a plane is initialised, so
 * pages without the module never load it.
 *
 * COMPLIANCE ( OneTrust / GDPR ): the Sketchfab iframe is a third party and
 * may set cookies once the model loads. If the consent policy requires it,
 * gate `Plane.initAll()` behind the relevant OneTrust consent group instead
 * of calling it unconditionally on DOMContentLoaded.
 *
 * Honors `prefers-reduced-motion`: autospin is forced off.
 */
import { PREFIX } from '../helpers/config.js';

const SELECTOR_ROOT = `[data-${PREFIX}-plane]`;

/**
 * Sketchfab Viewer API version and script endpoint. Pinned so the behaviour
 * is deterministic across deploys.
 *
 * @type {string}
 */
const SKETCHFAB_VERSION = '1.12.1';
const SKETCHFAB_API_URL = `https://static.sketchfab.com/api/sketchfab-viewer-${SKETCHFAB_VERSION}.js`;

/**
 * Shared promise that resolves once the Sketchfab Viewer API is available on
 * `window`, so the script is injected only once regardless of how many planes
 * live in the page.
 *
 * @type {Promise<Function>|null}
 */
let apiPromise = null;

/**
 * Whether the user asked for reduced motion.
 *
 * @returns {boolean}
 */
function prefersReducedMotion( ){
  return !!( window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches );
}

/**
 * Lazily loads the Sketchfab Viewer API and resolves with the global
 * `Sketchfab` constructor.
 *
 * @returns {Promise<Function>}
 */
function loadSketchfabApi( ){
  if( window.Sketchfab ){
    return Promise.resolve( window.Sketchfab );
  }
  if( apiPromise ){
    return apiPromise;
  }

  apiPromise = new Promise( ( resolve , reject ) => {
    const script = document.createElement( 'script' );
    script.src = SKETCHFAB_API_URL;
    script.async = true;
    script.onload = ( ) => {
      if( window.Sketchfab ){
        resolve( window.Sketchfab );
      } else {
        reject( new Error( 'Plane: Sketchfab API loaded but window.Sketchfab is missing' ) );
      }
    };
    script.onerror = ( ) => reject( new Error( 'Plane: failed to load the Sketchfab Viewer API' ) );
    document.head.appendChild( script );
  } );

  return apiPromise;
}

export class Plane {
  /**
   * @param {HTMLElement} element - The module section root.
   */
  constructor( element ){
    if( !( element instanceof HTMLElement ) ) throw new Error( 'Plane: elemento inválido' );

    this._element = element;
    this._reduced = prefersReducedMotion( );
    this._api     = null;
    this._started = false;

    this._frame   = element.querySelector( `[data-${PREFIX}-plane-frame]` );
    this._shield  = element.querySelector( `[data-${PREFIX}-plane-shield]` );
    this._release = element.querySelector( `[data-${PREFIX}-plane-release]` );
    this._uid     = element.getAttribute( `data-${PREFIX}-plane-uid` ) || '';
    this._auto    = element.getAttribute( `data-${PREFIX}-plane-autostart` ) === '1';

    if( !this._frame || !this._uid ){
      return;
    }

    // Click-to-interact shield: the single interaction surface. It swallows
    // pointer events so the page keeps scrolling; clicking it loads the model
    // on demand ( when autostart is off ) and hands control to the viewer.
    // Moving the pointer out of the module gives scroll back to the page.
    if( this._shield ){
      this._shield.addEventListener( 'click' , ( ) => this._onShieldClick( ) );
      element.addEventListener( 'mouseleave' , ( ) => this._deactivate( ) );
    }

    // Release button: the primary way back to page scroll on a full-bleed
    // module, where the pointer rarely leaves the section.
    if( this._release ){
      this._release.addEventListener( 'click' , ( ) => this._deactivate( ) );
    }

    loadSketchfabApi( )
      .then( ( Sketchfab ) => this._init( Sketchfab ) )
      .catch( ( error ) => this._fail( error ) );
  }

  /**
   * Reads the viewer options from the root `data-*` attributes. Note that
   * `autostart` is intentionally NOT forwarded: the load timing is controlled
   * by when `api.start()` is called ( see `_init` / `_begin` ).
   *
   * @returns {Object}
   */
  _options( ){
    const attr = ( name ) => this._element.getAttribute( `data-${PREFIX}-plane-${name}` );
    const showUi = attr( 'ui' ) === '1' ? 1 : 0;

    const options = {
      ui_controls: showUi ,
      ui_infos: showUi ,
      ui_annotations: attr( 'annotations' ) === '1' ? 1 : 0 ,
      // Transparent background so the module's own bgcolor shows through.
      transparent: 1
    };

    const autospin = parseFloat( attr( 'autospin' ) || '0' );
    options.autospin = ( !this._reduced && autospin > 0 ) ? autospin : 0;

    return options;
  }

  /**
   * Initialises the Sketchfab viewer on the module iframe. Stores the `api`
   * handle and either starts immediately ( autostart on ) or stays idle until
   * the shield is clicked ( autostart off ).
   *
   * @param {Function} Sketchfab - The global Sketchfab constructor.
   * @returns {void}
   */
  _init( Sketchfab ){
    const client = new Sketchfab( SKETCHFAB_VERSION , this._frame );

    client.init( this._uid , {
      ...this._options( ) ,
      success: ( api ) => {
        this._api = api;
        api.addEventListener( 'viewerready' , ( ) => this._ready( ) );
        if( this._auto ){
          this._begin( );
        } else {
          this._element.classList.add( 'is-idle' );
        }
      } ,
      error: ( ) => this._fail( new Error( 'Plane: Sketchfab viewer init error' ) )
    } );
  }

  /**
   * Shield click: loads the model on the first click ( when autostart is off )
   * and always hands pointer control to the viewer.
   *
   * @returns {void}
   */
  _onShieldClick( ){
    if( !this._started ){
      this._begin( );
    }
    this._activate( );
  }

  /**
   * Starts the viewer ( loads the 3D scene ). Idempotent: only the first call
   * has an effect. Used both for the autostart flow and the shield click.
   *
   * @returns {void}
   */
  _begin( ){
    if( !this._api || this._started ){
      return;
    }
    this._started = true;
    this._element.classList.remove( 'is-idle' );
    this._element.classList.add( 'is-loading' );
    this._api.start( );
  }

  /**
   * Hides the loader once the viewer is ready.
   *
   * @returns {void}
   */
  _ready( ){
    this._element.classList.remove( 'is-idle' , 'is-loading' );
    this._element.classList.add( 'is-ready' );
  }

  /**
   * Hands pointer control to the viewer ( shield lets events through ), so the
   * visitor can orbit, pan and wheel-zoom the model.
   *
   * @returns {void}
   */
  _activate( ){
    this._element.classList.add( 'is-active' );
  }

  /**
   * Returns pointer control to the page ( shield swallows events again ), so
   * the mouse wheel scrolls the page instead of zooming the model.
   *
   * @returns {void}
   */
  _deactivate( ){
    this._element.classList.remove( 'is-active' );
  }

  /**
   * Marks the module as failed, so a broken embed does not leave a spinner
   * running forever.
   *
   * @param {Error} error
   * @returns {void}
   */
  _fail( error ){
    this._element.classList.remove( 'is-idle' , 'is-loading' );
    this._element.classList.add( 'is-error' );
    if( window.console && console.warn ) console.warn( error );
  }

  /* ============
   * Init masiva
   * ===========*/
  static initAll( root = document ){
    const nodes = root.querySelectorAll( SELECTOR_ROOT );
    const instances = [ ];
    nodes.forEach( ( el ) => {
      if( el._planeInstance ) return;
      el._planeInstance = new Plane( el );
      instances.push( el._planeInstance );
    } );
    return instances;
  }
}

export default Plane;
